import React, { useState, useEffect } from 'react';
import { Play, Loader2, X, ChevronDown, ChevronRight, Key, AlertTriangle } from 'lucide-react';
import FormioRenderer from '../../presentation/components/formio-custom/FormioRenderer';
import { toast } from 'sonner';
import { getCamundaService } from '../../infrastructure/camunda';
import { getDesignerFormContent, parseFormioJson } from '../../services/taskService';

interface StartProcessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  definitionId: string;
  name: string;
  version: string;
  onStarted?: (instanceId: string) => void;
}

const StartProcessDialog: React.FC<StartProcessDialogProps> = ({
  isOpen,
  onClose,
  definitionId,
  name,
  version,
  onStarted,
}) => {
  const [businessKey, setBusinessKey] = useState('');
  const [startFormSchema, setStartFormSchema] = useState<any>(null);
  const [startFormLoading, setStartFormLoading] = useState(false);
  const [startingProcess, setStartingProcess] = useState(false);
  const [businessKeyOpen, setBusinessKeyOpen] = useState(false);

  /**
   * Diagnostic state for the start form load:
   *   - 'idle'      : not yet attempted
   *   - 'loading'   : fetch in progress
   *   - 'rendered'  : schema fetched and ready to render
   *   - 'none'      : process definition has no start form configured (this is OK)
   *   - 'error'     : a form was configured but we couldn't load it
   */
  const [startFormStatus, setStartFormStatus] = useState<
    'idle' | 'loading' | 'rendered' | 'none' | 'error'
  >('idle');
  const [startFormErrorMessage, setStartFormErrorMessage] = useState<string>('');
  const [rawFormKey, setRawFormKey] = useState<string>('');

  const camunda = getCamundaService();

  // Inject Bootstrap CSS + Formio overrides when dialog opens, clean up on close
  useEffect(() => {
    if (isOpen) {
      const link = document.createElement('link');
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css';
      link.rel = 'stylesheet';
      link.id = 'start-process-bootstrap-css';
      document.head.appendChild(link);

      const style = document.createElement('style');
      style.id = 'start-process-formio-overrides';
      style.textContent = `
        .start-process-form-container .formio-component {
          margin-bottom: 14px;
        }
        .start-process-form-container .formio-component label.col-form-label {
          font-weight: 600;
          font-size: 13px;
          color: #374151;
          margin-bottom: 4px;
          padding: 0;
        }
        .start-process-form-container .form-control {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 14px;
          transition: border-color 0.15s, box-shadow 0.15s;
          background: #f9fafb;
        }
        .start-process-form-container .form-control:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
          background: #fff;
          outline: none;
        }
        .start-process-form-container .form-check { padding-left: 0; }
        .start-process-form-container .form-check-input { margin-right: 8px; }
        .start-process-form-container select.form-control { appearance: auto; }
        .start-process-form-container textarea.form-control { min-height: 80px; resize: vertical; }
        .start-process-form-container .btn-primary,
        .start-process-form-container .btn-wizard-nav-submit {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 8px;
          padding: 10px 24px;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.15s;
          box-shadow: 0 1px 3px rgba(37,99,235,0.2);
        }
        .start-process-form-container .btn-primary:hover,
        .start-process-form-container .btn-wizard-nav-submit:hover {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          box-shadow: 0 2px 8px rgba(37,99,235,0.3);
          transform: translateY(-1px);
        }
        .start-process-form-container .btn-secondary,
        .start-process-form-container .btn-wizard-nav-previous {
          background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;
          border-radius: 8px; padding: 10px 24px; font-weight: 500; font-size: 14px;
          transition: all 0.15s;
        }
        .start-process-form-container .btn-secondary:hover,
        .start-process-form-container .btn-wizard-nav-previous:hover { background: #e5e7eb; }
        .start-process-form-container .btn-danger { border-radius: 8px; }
        .start-process-form-container .formio-component-submit .btn { width: 100%; padding: 12px 24px; }
        .start-process-form-container .card {
          border: 1px solid #e5e7eb; border-radius: 12px; box-shadow: none;
        }
        .start-process-form-container .card-header {
          background: #f9fafb; border-bottom: 1px solid #e5e7eb;
          border-radius: 12px 12px 0 0 !important;
          padding: 12px 16px; font-weight: 600; font-size: 14px;
        }
        .start-process-form-container .card-body { padding: 16px; }
        .start-process-form-container .table { font-size: 13px; }
        .start-process-form-container .alert { border-radius: 8px; font-size: 13px; }
        .start-process-form-container .formio-dialog { z-index: 10001; }
        .start-process-form-container .has-error .form-control { border-color: #ef4444; }
        .start-process-form-container .formio-errors .error { color: #ef4444; font-size: 12px; margin-top: 4px; }
        .start-process-form-container .help-block { font-size: 12px; color: #6b7280; margin-top: 4px; }
        .start-process-form-container .formio-wizard-nav-container {
          display: flex; gap: 8px; justify-content: flex-end; padding-top: 16px;
          border-top: 1px solid #e5e7eb; margin-top: 16px;
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.getElementById('start-process-bootstrap-css')?.remove();
        document.getElementById('start-process-formio-overrides')?.remove();
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !definitionId) return;

    setBusinessKey('');
    setStartFormSchema(null);
    setStartFormErrorMessage('');
    setRawFormKey('');
    setStartFormLoading(true);
    setStartFormStatus('loading');
    setBusinessKeyOpen(false);

    const fail = (message: string, raw?: string) => {
      console.warn('[StartProcessDialog]', message, raw ? { formKey: raw } : '');
      setStartFormErrorMessage(message);
      setStartFormStatus('error');
      setStartFormSchema(null);
    };

    const fetchStartForm = async () => {
      try {
        const formInfo = await camunda.processDefinition.getStartFormById(definitionId);
        const formKey = formInfo?.key?.trim() || '';
        setRawFormKey(formKey);
        console.log('[StartProcessDialog] fetched start form metadata', { definitionId, formInfo });

        // No start form configured at all — this is a valid state, not an error.
        if (!formKey) {
          setStartFormStatus('none');
          return;
        }

        // Designer-served form: api/form-contents/name/<slug> or api/form-contents/<id>
        if (formKey.startsWith('api/form-contents/')) {
          try {
            const designerResponse = await getDesignerFormContent(formKey);
            if (!designerResponse?.formioJson) {
              fail('Start form metadata returned but the designer service returned no schema.', formKey);
              return;
            }
            const schema = parseFormioJson(designerResponse.formioJson);
            if (!schema) {
              fail('Failed to parse the form schema returned by the designer service.', formKey);
              return;
            }
            setStartFormSchema(schema);
            setStartFormStatus('rendered');
          } catch (err: any) {
            console.error('[StartProcessDialog] designer fetch failed', err);
            fail(err?.message || 'Failed to load start form from the designer service.', formKey);
          }
          return;
        }

        // Camunda-deployed form (start-form attached as a deployment resource).
        // Camunda 7 emits keys like `embedded:deployment:forms/start.html`. We
        // don't currently render these, but tell the user instead of staying silent.
        if (formKey.startsWith('embedded:deployment:') || formKey.startsWith('camunda-forms:')) {
          fail(
            'This process uses a Camunda-deployed start form, which is not supported by ' +
                'the studio runtime. Re-create the form in the designer and assign it via the modeler.',
            formKey,
          );
          return;
        }

        // Anything else — most likely the modeler stored a raw form name or a custom URI.
        fail(
          `Unrecognised form key "${formKey}". Expected one starting with "api/form-contents/". ` +
              'Open the workflow in the modeler and reassign the form on the start event.',
          formKey,
        );
      } catch (err: any) {
        console.error('[StartProcessDialog] start-form metadata fetch failed', err);
        fail(err?.message || 'Failed to load start form metadata from Camunda.');
      } finally {
        setStartFormLoading(false);
      }
    };

    fetchStartForm();
  }, [isOpen, definitionId, camunda]);

  const handleStartProcess = async (formData?: any) => {
    try {
      setStartingProcess(true);

      let variables: Record<string, { value: unknown }> | undefined = undefined;

      if (formData && typeof formData === 'object') {
        variables = {};
        Object.keys(formData).forEach(key => {
          if (key !== 'submit') {
            variables![key] = { value: formData[key] };
          }
        });
      }

      const instance = await camunda.processDefinition.startById(definitionId, {
        businessKey: businessKey || undefined,
        variables
      });

      toast.success(`Process instance started: ${instance.id}`);
      onStarted?.(instance.id);
      onClose();
    } catch (error) {
      console.error('Error starting process:', error);
      toast.error('Failed to start process instance');
    } finally {
      setStartingProcess(false);
    }
  };

  if (!isOpen) return null;

  const hasForm = !!startFormSchema;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 md:p-6 lg:p-8"
      onClick={onClose}
      style={{ animation: 'spd-fadeIn 0.15s ease-out' }}
    >
      <style>{`
        @keyframes spd-fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes spd-slideUp { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden"
        style={{
          maxWidth: hasForm ? '900px' : '480px',
          animation: 'spd-slideUp 0.2s ease-out',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-xl shadow-sm">
              <Play size={18} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Start Process</h2>
              <p className="text-xs text-gray-500">{name} <span className="text-gray-400">v{version}</span></p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/80 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body - scrollable */}
        <div className="overflow-y-auto flex-1" style={{ minHeight: 0 }}>
          {startFormLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 size={28} className="animate-spin text-blue-500" />
              <span className="text-sm text-gray-400">Loading start form...</span>
            </div>
          ) : (
            <div className="p-6">
              {/* Business Key - collapsible when form exists, always visible otherwise */}
              {hasForm ? (
                <div className="mb-4">
                  <button
                    onClick={() => setBusinessKeyOpen(!businessKeyOpen)}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors w-full"
                  >
                    {businessKeyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Key size={13} className="text-gray-400" />
                    <span className="font-medium">Business Key</span>
                    {businessKey && !businessKeyOpen && (
                      <span className="ml-auto text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{businessKey}</span>
                    )}
                  </button>
                  {businessKeyOpen && (
                    <div className="mt-2 ml-7">
                      <input
                        type="text"
                        value={businessKey}
                        onChange={(e) => setBusinessKey(e.target.value)}
                        placeholder="e.g., ORDER-2024-001"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all"
                      />
                      <p className="mt-1 text-xs text-gray-400">
                        Optional identifier for this process instance.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Business Key <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={businessKey}
                    onChange={(e) => setBusinessKey(e.target.value)}
                    placeholder="e.g., ORDER-2024-001"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all"
                  />
                  <p className="mt-1.5 text-xs text-gray-400">
                    A business key helps identify this process instance in your business context.
                  </p>
                </div>
              )}

              {/* Form-load error: surface the reason so users know why the form area is empty */}
              {startFormStatus === 'error' && (
                <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 flex gap-2 text-sm">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-amber-800">
                    <div className="font-medium mb-0.5">Start form could not be loaded.</div>
                    <div className="text-amber-700/90 leading-snug">{startFormErrorMessage}</div>
                    {rawFormKey && (
                      <div className="mt-1 text-xs text-amber-600/80 font-mono break-all">
                        formKey: {rawFormKey}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-amber-700/80">
                      You can still start the process below without the form.
                    </div>
                  </div>
                </div>
              )}

              {/* Form */}
              {hasForm && (
                <div className="start-process-form-container">
                  <FormioRenderer
                    form={startFormSchema}
                    onSubmit={(submission: any) => handleStartProcess(submission.data)}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - only show Start button when there's no form (form has its own submit) */}
        {!startFormLoading && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/80 flex gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 bg-white transition-colors"
            >
              Cancel
            </button>
            {!hasForm && (
              <button
                onClick={() => handleStartProcess()}
                disabled={startingProcess}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 shadow-sm transition-all"
              >
                {startingProcess ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Play size={16} />
                )}
                Start Process
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StartProcessDialog;
